import {useEffect,useRef,useState} from 'react';
import {CompanyFileIntake} from './CompanyFileIntake';
import {acceptCustomerSubmission,loadCustomerSubmissions,CompanyFileReceipt,CustomerSubmission} from './companyOperationsApi';
import type {NewCompanyFileInput} from './carolinaWorkflowStore';
import {goToCompanyTask} from './companyTaskNavigation';
import {IntakeAttachment,IntakeDocumentAttachments} from './IntakeDocumentAttachments';

function readableSubmission(value:unknown):value is CustomerSubmission {
  if(!value||typeof value!=='object'||Array.isArray(value))return false;
  const row=value as Record<string,unknown>;
  const id=(v:unknown)=>typeof v==='string'&&v.length===36&&/^[a-f0-9]{8}-[a-f0-9]{4}-[1-5][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/.test(v);
  return id(row.authorization_id)&&id(row.order_id)
    &&['authorization_version','details_version','order_version'].every(key=>Number.isSafeInteger(row[key])&&Number(row[key])>0)
    &&(row.authorization_state==='pending_acceptance'||row.authorization_state==='active')
    &&['customer_name','client_reference','case_caption','recipient_name','address_line_1','city','admin_area','postal_code','country_code','deadline_date'].every(key=>typeof row[key]==='string')
    &&['case_number','court_reference','address_line_2'].every(key=>row[key]===null||typeof row[key]==='string')
    &&Array.isArray(row.documents_to_serve)&&row.documents_to_serve.every(title=>typeof title==='string'&&title.trim()!=='');
}

export function CustomerOrderIntake({onCreated}:{onCreated:()=>void}) {
  const [items,setItems]=useState<CustomerSubmission[]|null>(null);
  const [selected,setSelected]=useState<CustomerSubmission|null>(null);
  const [receipt,setReceipt]=useState<CompanyFileReceipt|null>(null);
  const [attachment,setAttachment]=useState<IntakeAttachment|null>(null);
  const [error,setError]=useState('');
  const [reload,setReload]=useState(0);
  const heading=useRef<HTMLHeadingElement>(null);
  useEffect(()=>{let active=true;setItems(null);setError('');
    const finish=(rows:CustomerSubmission[]|null)=>{
      if(!active)return;active=false;clearTimeout(timer);
      if(rows===null)setError('Customer submissions could not be loaded. Please reload to try again.');else setItems(rows);
    };
    const timer=setTimeout(()=>finish(null),15000);
    loadCustomerSubmissions().then(rows=>{
    if(!rows.every(readableSubmission)||new Set(rows.map(row=>row.authorization_id)).size!==rows.length)throw new Error('Customer submission readback mismatch.');
    finish(rows);
  }).catch(()=>finish(null));return()=>{active=false;clearTimeout(timer)}},[reload]);
  useEffect(()=>{heading.current?.focus();heading.current?.scrollIntoView?.({block:'start'})},[receipt,selected]);
  async function save(file:NewCompanyFileInput,_key:string,documents?:File[]) {
    if(!selected)throw new Error('Select a customer submission.');
    const saved=await acceptCustomerSubmission(selected,file);
    setReceipt(saved);
    if(documents&&documents.length)setAttachment({fileId:saved.company_file_id,files:documents,jobCount:saved.jobs.length});
    onCreated();
    return saved.company_file_id;
  }
  function list(){setAttachment(null);setSelected(null);setReceipt(null);setReload(n=>n+1)}
  if(receipt)return <section className="panel">
    <h2 ref={heading} tabIndex={-1}>Customer submission accepted</h2>
    <p>File <strong>{receipt.file_number}</strong> is saved and ready for assignment.</p>
    <ul>{receipt.jobs.map(job=><li key={job.service_job_id}>Job <strong>{job.job_number}</strong> — {job.subject_name} <button onClick={()=>goToCompanyTask('/company/jobs/'+job.service_job_id+'?from=unassigned')}>Open job</button></li>)}</ul>
    <p>No server has been assigned or dispatched. Check the service documents before dispatch.</p>
    {attachment&&<IntakeDocumentAttachments attachment={attachment} onDismiss={()=>setAttachment(null)}/>}
    <button onClick={list}>Review another submission</button>
    <button onClick={()=>goToCompanyTask('/')}>Back to Operations</button>
  </section>;
  if(selected){
    const values:Partial<NewCompanyFileInput>={caption:selected.case_caption,caseNumber:selected.case_number??'',court:selected.court_reference??'',originatingCustomer:selected.customer_name,sourceOrderReference:selected.client_reference,
      jobs:[{subject:selected.recipient_name,serviceAddress:[selected.address_line_1,selected.address_line_2,selected.city,selected.admin_area,selected.postal_code,selected.country_code].filter(Boolean).join(', '),deadlineDate:selected.deadline_date,serviceLevel:'Standard',server:'Unassigned',documentsToServe:selected.documents_to_serve}]};
    return <section>
      <h2 ref={heading} tabIndex={-1}>Review customer submission</h2>
      <p><strong>{selected.customer_name}</strong> · Reference {selected.client_reference}</p>
      <p>Submitted case: {selected.case_caption}</p>
      <p>Check the supplied details, enter the plaintiff, defendant and issuing court state, and review the documents to serve. Saving accepts this submission and creates the company file and jobs.</p>
      <p>Document titles are included below. After saving, open the job to add the customer’s uploads, scan them and release the service papers before dispatch.</p>
      <CompanyFileIntake sourceLocked key={selected.authorization_id} initialValues={values} openRequest={1} liveCreate={save} onCreated={()=>{}} onCancel={list}/>
    </section>;
  }
  return <section className="panel">
    <h2 ref={heading} tabIndex={-1}>Customer submissions</h2>
    <p>Review work authorized for your company before creating its file and service jobs.</p>
    {error&&<p role="alert">{error}</p>}
    {!items&&!error&&<p role="status">Loading customer submissions…</p>}
    <button onClick={()=>setReload(n=>n+1)}>Reload submissions</button>
    {items?.length===0&&<p>No customer submissions are awaiting intake.</p>}
    <ul>{items?.map(item=><li key={item.authorization_id}>
      <h3>{item.client_reference} — {item.recipient_name}</h3>
      <p>{item.customer_name} · {item.case_caption}</p>
      <p>Service deadline: {item.deadline_date} · {item.city}, {item.admin_area}</p>
      <button onClick={()=>setSelected(item)}>Review submission</button>
    </li>)}</ul>
  </section>;
}
